import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const RECENT_KEY = 'cst-recent-searches';
const MOBILE = window.matchMedia('(max-width: 749px)');
const RECENT_LIMIT = 6;

/** @returns {string[]} */
function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(value) ? value.filter((term) => typeof term === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string} term */
function rememberSearch(term) {
  const clean = term.trim().replace(/\s+/g, ' ').slice(0, 60);
  if (!clean) return;
  const next = [clean, ...readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase())].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked (private mode): recent searches simply stay hidden.
  }
}

/**
 * Opens and closes the inline header search results panel.
 * Searching itself is done by Horizon's <predictive-search-component> inside this element;
 * this toggles the `open` attribute that shows the panel under the header, and fills the
 * "recent searches" chips from terms saved whenever a search results page loads.
 *
 * Only a real user action opens it (pointer press inside, Tab into it, typing). Horizon's
 * predictive search calls `input.focus()` on its own when it loads the "Recently viewed" empty
 * state after page load; in the stock modal that focus is harmless because the dialog is closed,
 * but here it would open the panel and steal page focus, so that stray focus is undone.
 *
 * Mobile: the bar is hidden and Horizon's search icon stays in the header. A tap on that icon is
 * intercepted and opens this form as a full-screen popover instead of Horizon's modal. The popover
 * lives in the top layer, so the header's `contain: layout` cannot clip it. Browsers without the
 * Popover API fall through to Horizon's modal.
 */
class CstHeaderSearchComponent extends Component {
  #controller = new AbortController();

  /** True when the latest user interaction could legitimately move focus into the bar. */
  #userIntent = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.#recordCurrentSearch();
    this.#renderRecent();

    const results = this.querySelector('#cst-header-search-results');
    if (results) {
      this.#resultsObserver = new MutationObserver(() => this.#trimResults(results));
      this.#resultsObserver.observe(results, { childList: true, subtree: true });
    }

    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('input', this.#open, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal, capture: true });
    document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal, capture: true });
    // Window capture runs before Component's document-level `on:keydown` delegation.
    window.addEventListener('keydown', this.#releaseTab, { signal, capture: true });
    // Window capture runs before the `on:click="#search-modal/showDialog"` delegation.
    window.addEventListener('click', this.#handleSearchIconClick, { signal, capture: true });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('pointerdown', this.#startDragScroll, { signal });
    // Capture phase so a drag that ends on a pill does not also follow its link.
    this.addEventListener('click', this.#swallowClickAfterDrag, { signal, capture: true });
    MOBILE.addEventListener('change', () => this.#close(), { signal });
    window.addEventListener('resize', this.#measurePanel, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resultsObserver?.disconnect();
  }

  /** @type {MutationObserver | null} */
  #resultsObserver = null;

  /**
   * The panel must not scroll: keep one row of products (the same count as the idle product
   * row) and the collections carousel; drop the pages and articles carousels. Items are removed,
   * not hidden, so Horizon's arrow-key navigation never lands on something invisible.
   * @param {Element} results
   */
  #trimResults(results) {
    const limit = Number(this.dataset.productLimit) || 5;

    for (const list of results.querySelectorAll('.predictive-search-results__wrapper-products')) {
      [...list.children].slice(limit).forEach((item) => item.remove());
    }

    const inner = results.querySelector('.predictive-search-results__inner');
    if (!inner) return;
    for (const card of results.querySelectorAll('[data-resource-type="page"], [data-resource-type="article"]')) {
      let block = card;
      while (block.parentElement && block.parentElement !== inner) block = block.parentElement;
      if (block.parentElement === inner) block.remove();
    }
  }

  /** The input belongs to the nested predictive-search component's refs, so query it directly. */
  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="search"]'));
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('form[popover]'));
  }

  /** @type {string | null} */
  #desktopPlaceholder = null;

  #openMobile() {
    const form = this.#form;
    const input = this.#input;
    if (!form || !input || typeof form.showPopover !== 'function') return false;

    if (!form.matches(':popover-open')) form.showPopover();
    this.setAttribute('open', '');
    input.setAttribute('aria-expanded', 'true');
    this.#lockScroll();

    const mobilePlaceholder = this.dataset.mobilePlaceholder;
    if (mobilePlaceholder) {
      this.#desktopPlaceholder ??= input.placeholder;
      input.placeholder = mobilePlaceholder;
    }

    this.#userIntent = true;
    input.focus();
    return true;
  }

  /**
   * Tap on Horizon's header search icon (mobile only): open this full-screen search instead.
   * @param {MouseEvent} event
   */
  #handleSearchIconClick = (event) => {
    if (!MOBILE.matches || !(event.target instanceof Element)) return;
    const icon = event.target.closest('search-button');
    if (!icon || this.contains(icon)) return;
    if (typeof this.#form?.showPopover !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    this.#openMobile();
  };

  /** True for the click that ends a mouse drag on a scroll row. */
  #dragged = false;

  /**
   * Mouse drag-to-scroll for the one-line suggestion pills (touch scrolls natively).
   * @param {PointerEvent} event
   */
  #startDragScroll = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !(event.target instanceof Element)) return;
    const row = event.target.closest('.predictive-search-results__wrapper-queries');
    if (!(row instanceof HTMLElement) || row.scrollWidth <= row.clientWidth) return;

    const startX = event.clientX;
    const startScroll = row.scrollLeft;
    this.#dragged = false;

    /** @param {PointerEvent} moveEvent */
    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (!this.#dragged && Math.abs(delta) < 5) return;
      if (!this.#dragged) {
        this.#dragged = true;
        row.classList.add('is-dragging');
      }
      moveEvent.preventDefault();
      row.scrollLeft = startScroll - delta;
    };

    const onUp = () => {
      row.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      // The click (if any) fires right after pointerup; clear the flag once it has been handled.
      setTimeout(() => (this.#dragged = false), 0);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  /** @param {MouseEvent} event */
  #swallowClickAfterDrag = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-cst-close]')) {
      this.#close();
      return;
    }

    if (event.target.closest('[data-cst-recent-clear]')) {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch {
        // Storage blocked: nothing stored to clear.
      }
      this.#renderRecent();
      this.#input?.focus();
      return;
    }

    const fill = event.target.closest('[data-cst-fill]');
    const input = this.#input;
    if (fill instanceof HTMLElement && input && fill.dataset.term) {
      // Put the term in the field and let Horizon search it (its handler needs an inputType).
      input.value = fill.dataset.term;
      input.focus();
      input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: fill.dataset.term }));
    }
  };

  /** Every route to results (Search button, Enter, View all, chips) lands on /search?q=… */
  #recordCurrentSearch() {
    const url = new URL(window.location.href);
    const term = url.searchParams.get('q');
    if (term && url.pathname.replace(/\/$/, '').endsWith('/search')) rememberSearch(term);
  }

  #renderRecent() {
    const group = this.querySelector('[data-cst-recent]');
    const list = this.querySelector('[data-cst-recent-list]');
    const template = this.querySelector('template[data-cst-recent-template]');
    if (!(group instanceof HTMLElement) || !list || !(template instanceof HTMLTemplateElement)) return;

    const terms = readRecent();
    list.replaceChildren(
      ...terms.map((term) => {
        const item = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const link = item.querySelector('a');
        const label = item.querySelector('[data-term]');
        const fill = item.querySelector('[data-cst-fill]');
        if (fill instanceof HTMLElement) {
          fill.dataset.term = term;
          fill.setAttribute('aria-label', `${this.dataset.fillLabel || ''}: ${term}`);
        }
        if (link) {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('q', term);
          url.searchParams.set('options[prefix]', 'last');
          link.href = url.toString();
        }
        if (label) label.textContent = term;
        return item;
      })
    );
    group.hidden = terms.length === 0;
  }

  #open = () => {
    if (this.hasAttribute('open')) return;
    this.setAttribute('open', '');
    this.#input?.setAttribute('aria-expanded', 'true');
    this.#measurePanel();
    this.#lockScroll();
  };

  /** Desktop: cap the panel at the visible height below the header (CSS reads --cst-panel-top). */
  #measurePanel = () => {
    if (!this.hasAttribute('open') || MOBILE.matches) return;
    const panel = this.querySelector('.cst-search-panel');
    if (!panel) return;
    const top = Math.max(0, Math.round(panel.getBoundingClientRect().top));
    this.style.setProperty('--cst-panel-top', `${top}px`);
  };

  /**
   * Stop the page behind the search from scrolling. Horizon scrolls `.page-wrapper` (not the
   * window) on desktop, so use its own lock, which sets `html[scroll-lock]` and covers both.
   */
  #lockScroll() {
    lockScroll(this);
  }

  #unlockScroll() {
    unlockScroll(this);
  }

  #close = () => {
    const form = this.#form;
    if (form?.matches(':popover-open')) form.hidePopover();
    this.#unlockScroll();
    if (this.#input && this.#desktopPlaceholder !== null) {
      this.#input.placeholder = this.#desktopPlaceholder;
      this.#desktopPlaceholder = null;
    }

    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.#input?.setAttribute('aria-expanded', 'false');
  };

  #handleFocusIn = () => {
    if (this.hasAttribute('open')) return;

    if (this.#userIntent) {
      this.#open();
    } else if (document.activeElement === this.#input) {
      // Programmatic focus (not from the user): give focus back to the page.
      this.#input?.blur();
    }
  };

  /**
   * Close when keyboard focus leaves the component. A null relatedTarget means a click on
   * something that does not take focus (Safari buttons/links), handled by pointerdown instead.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !this.contains(next)) this.#close();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    const inside = event.target instanceof Node && this.contains(event.target);
    this.#userIntent = inside;
    if (!inside) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #handleDocumentKeyDown = (event) => {
    const inside = event.target instanceof Node && this.contains(event.target);
    this.#userIntent = inside || event.key === 'Tab';
  };

  /**
   * Horizon's onSearchKeyDown turns Tab into "next result", which is right inside the modal but
   * traps keyboard users in an inline header field. Keep Tab native here; arrows still move
   * through the results.
   * @param {KeyboardEvent} event
   */
  #releaseTab = (event) => {
    if (event.key === 'Tab' && event.target instanceof Node && this.contains(event.target)) {
      event.stopPropagation();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') this.#close();
  };
}

if (!customElements.get('cst-header-search-component')) {
  customElements.define('cst-header-search-component', CstHeaderSearchComponent);
}
